import { useEffect, useRef, useState } from "react";

const isVisible = () => typeof document === "undefined" || document.visibilityState === "visible";

/**
 * Runs `callback` every `delayMs` while `enabled` is true and the tab is
 * visible (ASB-4295's report polling). A hidden tab stops the timer and a
 * visible one starts it again, waiting a full delay before the next call. A
 * tick is skipped while the previous call's promise is still pending, so a
 * slow response never stacks requests. Stops on unmount, and whenever
 * `enabled` turns false or `delayMs` changes.
 *
 * The callback may change between renders; the newest one is called.
 */
export default function useVisibleInterval(callback, delayMs, enabled) {
	const callbackRef = useRef(callback);
	callbackRef.current = callback;
	const [visible, setVisible] = useState(isVisible);

	useEffect(() => {
		const onChange = () => setVisible(isVisible());
		document.addEventListener("visibilitychange", onChange);
		return () => document.removeEventListener("visibilitychange", onChange);
	}, []);

	useEffect(() => {
		if (!enabled || !visible) return undefined;
		let inFlight = false;
		let stopped = false;
		const id = setInterval(async () => {
			if (inFlight || stopped) return;
			inFlight = true;
			try {
				await callbackRef.current();
			} catch (error) {
				// The callback owns its errors; a throw must not stop the timer.
			} finally {
				inFlight = false;
			}
		}, delayMs);
		return () => {
			stopped = true;
			clearInterval(id);
		};
	}, [enabled, visible, delayMs]);
}
